/**
 * NCAABAnalyticsContent - Tufte-inspired NCAAB game analytics page.
 *
 * Single scrollable layout (no tabs) since NCAAB has no player data.
 * Composes all Tufte components with NCAAB-specific data mapping.
 *
 * This is the ONLY sport-specific file in the Tufte system.
 * All imported components are sport-agnostic and reusable for NBA/NHL/NFL.
 *
 * @param {Object} game - Full game summary from /ncaa-b/games/{id}/summary
 * @param {string} sport - Sport identifier ('ncaa-b')
 */
import { useMemo } from 'react';
import {
  GamePerformanceStripCard,
  PredictionPanel,
  TeamStrengthComparison,
  ScoringTrendStrip,
  SmallMultiplesGrid,
  BettingContextStrip,
} from './tufte';
import Card from '../common/Card';

const NCAABAnalyticsContent = ({ game, sport }) => {
  const homeTeam = game?.home_team;
  const awayTeam = game?.away_team;
  const homeStats = game?.home_stats;
  const awayStats = game?.away_stats;
  const predictions = game?.predictions;
  const h2h = game?.head_to_head;

  // Build PPG trend data from recent games for sparklines
  const homePpgTrend = useMemo(() => {
    const recent = homeTeam?.recent_games || [];
    return recent.map(g => g.team_score).filter(Boolean);
  }, [homeTeam]);

  const awayPpgTrend = useMemo(() => {
    const recent = awayTeam?.recent_games || [];
    return recent.map(g => g.team_score).filter(Boolean);
  }, [awayTeam]);

  // Build PAPG trend data
  const homePapgTrend = useMemo(() => {
    const recent = homeTeam?.recent_games || [];
    return recent.map(g => g.opp_score).filter(Boolean);
  }, [homeTeam]);

  const awayPapgTrend = useMemo(() => {
    const recent = awayTeam?.recent_games || [];
    return recent.map(g => g.opp_score).filter(Boolean);
  }, [awayTeam]);

  // Build point diff trend
  const homeDiffTrend = useMemo(() => {
    const recent = homeTeam?.recent_games || [];
    return recent
      .filter(g => g.team_score != null && g.opp_score != null)
      .map(g => g.team_score - g.opp_score);
  }, [homeTeam]);

  const awayDiffTrend = useMemo(() => {
    const recent = awayTeam?.recent_games || [];
    return recent
      .filter(g => g.team_score != null && g.opp_score != null)
      .map(g => g.team_score - g.opp_score);
  }, [awayTeam]);

  // Map NCAAB data to TeamStrengthComparison metrics
  const strengthMetrics = useMemo(() => {
    const hr = homeStats?.rankings || {};
    const ar = awayStats?.rankings || {};

    return [
      {
        label: 'BPI Rank',
        homeValue: hr.bpi_rank,
        awayValue: ar.bpi_rank,
        min: 1,
        max: 363,
        lowerIsBetter: true,
        format: (v) => v != null ? `#${v}` : '-',
      },
      {
        label: 'SOS Rank',
        homeValue: hr.sos_rank,
        awayValue: ar.sos_rank,
        min: 1,
        max: 363,
        lowerIsBetter: true,
        format: (v) => v != null ? `#${v}` : '-',
      },
      {
        label: 'Quality W-L',
        homeValue: hr.quality_wins,
        awayValue: ar.quality_wins,
        min: 0,
        max: 15,
        format: (v) => {
          if (v == null) return '-';
          return `${v}`;
        },
      },
      {
        label: 'Off Rating',
        homeValue: homeStats?.advanced?.offensive_rating,
        awayValue: awayStats?.advanced?.offensive_rating,
        min: 80,
        max: 130,
        format: (v) => v != null ? v.toFixed(1) : '-',
      },
      {
        label: 'Def Rating',
        homeValue: homeStats?.advanced?.defensive_rating,
        awayValue: awayStats?.advanced?.defensive_rating,
        min: 80,
        max: 130,
        lowerIsBetter: true,
        format: (v) => v != null ? v.toFixed(1) : '-',
      },
      {
        label: 'Net Rating',
        homeValue: homeStats?.advanced?.net_rating,
        awayValue: awayStats?.advanced?.net_rating,
        min: -30,
        max: 30,
        format: (v) => v != null ? (v > 0 ? `+${v.toFixed(1)}` : v.toFixed(1)) : '-',
      },
    ];
  }, [homeStats, awayStats]);

  // Map scoring data to ScoringTrendStrip rows
  const scoringRows = useMemo(() => {
    const hs = homeStats?.season || {};
    const as_ = awayStats?.season || {};
    const hPpg = hs.ppg || 0;
    const aPpg = as_.ppg || 0;
    const hPapg = hs.papg || 0;
    const aPapg = as_.papg || 0;

    return [
      {
        label: 'PPG',
        homeValue: hPpg,
        awayValue: aPpg,
        homeSparkData: homePpgTrend,
        awaySparkData: awayPpgTrend,
      },
      {
        label: 'PAPG',
        homeValue: hPapg,
        awayValue: aPapg,
        homeSparkData: homePapgTrend,
        awaySparkData: awayPapgTrend,
      },
      {
        label: 'Diff',
        homeValue: Math.round((hPpg - hPapg) * 10) / 10,
        awayValue: Math.round((aPpg - aPapg) * 10) / 10,
        homeSparkData: homeDiffTrend,
        awaySparkData: awayDiffTrend,
        homeColor: hPpg - hPapg > 0 ? 'text-success' : 'text-danger',
        awayColor: aPpg - aPapg > 0 ? 'text-success' : 'text-danger',
      },
    ];
  }, [homeStats, awayStats, homePpgTrend, awayPpgTrend, homePapgTrend, awayPapgTrend, homeDiffTrend, awayDiffTrend]);

  // Map advanced stats to SmallMultiplesGrid
  const advancedMetrics = useMemo(() => {
    const ha = homeStats?.advanced || {};
    const aa = awayStats?.advanced || {};
    const pctFormat = (v) => v != null ? `${(v * 100).toFixed(1)}%` : '-';
    const ratioFormat = (v) => v != null ? v.toFixed(2) : '-';
    const paceFormat = (v) => v != null ? v.toFixed(1) : '-';

    return [
      { label: 'eFG%', homeValue: ha.effective_fg_pct, awayValue: aa.effective_fg_pct, max: 0.65, format: pctFormat },
      { label: 'TS%', homeValue: ha.true_shooting_pct, awayValue: aa.true_shooting_pct, max: 0.65, format: pctFormat },
      { label: '3P Rate', homeValue: ha.three_point_rate, awayValue: aa.three_point_rate, max: 0.50, format: pctFormat },
      { label: 'A/TO', homeValue: ha.assist_to_turnover, awayValue: aa.assist_to_turnover, max: 2.5, format: ratioFormat },
      { label: 'TO%', homeValue: ha.turnover_pct, awayValue: aa.turnover_pct, max: 25, format: (v) => v != null ? `${v.toFixed(1)}%` : '-' },
      { label: 'Pace', homeValue: ha.pace, awayValue: aa.pace, max: 80, format: paceFormat },
    ];
  }, [homeStats, awayStats]);

  // Map betting records to BettingContextStrip
  const bettingRecords = useMemo(() => {
    const hs = homeStats?.season || {};
    const as_ = awayStats?.season || {};

    return [
      {
        label: 'Against the Spread',
        wins: hs.ats_wins,
        losses: hs.ats_losses,
        pushes: hs.ats_pushes,
        awayWins: as_.ats_wins,
        awayLosses: as_.ats_losses,
        awayPushes: as_.ats_pushes,
      },
      {
        label: 'Over/Under',
        wins: hs.over_count,
        losses: hs.under_count,
        pushes: hs.push_count,
        awayWins: as_.over_count,
        awayLosses: as_.under_count,
        awayPushes: as_.push_count,
      },
    ];
  }, [homeStats, awayStats]);

  if (!game) return null;

  return (
    <div className="space-y-4">
      {/* 1. Game Performance Strips - side by side */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <GamePerformanceStripCard
          team={awayTeam}
          stats={awayStats}
          momentum={awayTeam?.momentum}
          recentGames={awayTeam?.recent_games || []}
          ppgTrend={awayPpgTrend}
        />
        <GamePerformanceStripCard
          team={homeTeam}
          stats={homeStats}
          momentum={homeTeam?.momentum}
          recentGames={homeTeam?.recent_games || []}
          ppgTrend={homePpgTrend}
        />
      </div>

      {/* 2. Strength + Scoring side by side to reduce empty space */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <TeamStrengthComparison
          metrics={strengthMetrics}
          homeTeam={homeTeam}
          awayTeam={awayTeam}
        />
        <ScoringTrendStrip
          rows={scoringRows}
          homeTeam={homeTeam}
          awayTeam={awayTeam}
        />
      </div>

      {/* 3. Advanced metrics small multiples */}
      <SmallMultiplesGrid
        metrics={advancedMetrics}
        homeTeam={homeTeam}
        awayTeam={awayTeam}
      />

      {/* 4. Betting context + Head-to-Head side by side */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <BettingContextStrip
          records={bettingRecords}
          homeTeam={homeTeam}
          awayTeam={awayTeam}
        />

        {/* Head-to-Head (if exists) */}
        {h2h && (h2h.wins > 0 || h2h.losses > 0) && (
          <Card className="p-3">
            <h3 className="text-xs font-semibold text-text-secondary uppercase tracking-wider mb-2">
              Head-to-Head
            </h3>
            <div className="flex items-center gap-4 text-xs">
              <span className="text-text-secondary">
                {awayTeam?.abbreviation || 'Away'}: <span className="font-mono font-semibold text-text-primary">{h2h.losses}</span> wins
              </span>
              <span className="text-text-secondary">
                {homeTeam?.abbreviation || 'Home'}: <span className="font-mono font-semibold text-text-primary">{h2h.wins}</span> wins
              </span>
              {h2h.last_date && (
                <span className="text-text-secondary text-[10px] opacity-60 ml-auto">
                  Last: {h2h.last_date} ({h2h.last_result === 'W' ? homeTeam?.abbreviation : awayTeam?.abbreviation} won)
                </span>
              )}
            </div>
          </Card>
        )}
      </div>

      {/* 5. Predictions at bottom (premium-gated) */}
      {predictions && (
        <PredictionPanel
          predictions={predictions}
          spread={game.spread}
          spreadHomeOdds={game.spread_home_odds}
          spreadAwayOdds={game.spread_away_odds}
          overUnder={game.over_under}
          overOdds={game.over_odds}
          underOdds={game.under_odds}
          homeMoneyline={game.home_moneyline}
          awayMoneyline={game.away_moneyline}
          homeTeam={homeTeam}
          awayTeam={awayTeam}
        />
      )}
    </div>
  );
};

export default NCAABAnalyticsContent;
